import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  VStack,
  Text,
  Spinner,
  Center,
  HStack,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  Select,
  Icon,
} from "@chakra-ui/react";
import { ArrowBackIcon, DeleteIcon, SearchIcon, AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { getModelById } from "../../services/DataModelService";
import { Header, ModelInfo } from "build-ts/metadata";
import { BiNetworkChart } from "react-icons/bi";
import { useConfigurationContext } from "context/ConfigurationContext";
import CustomModal from "../modals/CustomModal";
import EditMetricDefinitionContainer from "../../containers/configuration/EditMetricDefinitionContainer";
import MKGVisualizerContainer from "../../containers/configuration/mkg/MKGVisualizerContainer";
import { FaCheckCircle, FaEye, FaFilter } from "react-icons/fa";
import EditMKGContainer from "../../containers/configuration/mkg/EditMKGContainer";
import DependenciesModal from "./DependenciesModal";
import { MdBarChart, MdEdit } from "react-icons/md";

interface MetricListComponentProps {
  onBack: () => void;
  onReloadKnowledgeGraph?: () => void;
};

const MetricListComponent: React.FC<MetricListComponentProps> = ({ 
    onBack,
    onReloadKnowledgeGraph
 }) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const {
    fetchAllMetrics,
    metricList,
    modelList,
    selectedMetric,
    setSelectedMetric,
    setSelectedBenchmark,
    setSelectedKgColumn,
    handleEditMetricDefinitionModal,
    handleEditMKGModal,
    handleDeleteMetricDialog,
    handleDeleteMetric,
    editMKG,
    viewMKG,
    handleViewMKGModal,
    editMetricDefinition,
    deleteMetricDialog,
    metricModalState,
    closeModal,
    metricInfo,
    closeDeleteMetricDialog,
    closeDeleteMetricModal,
    dependencies,
    showDependenciesModal,
  } = useConfigurationContext();

  const cancelDeleteRef = React.useRef<HTMLButtonElement>(null);
  const [createMetricFlag, setCreateMetricFlag] = useState<boolean>(false);
  const [modelInfoData, setModelInfoData] = useState<ModelInfo | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string>("");
  const [openCreateMetricForm, setOpenCreateMetricForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [triggerSubmit, setTriggerSubmit] = useState<boolean>(false);

useEffect(() => {
    const loadMetrics = async () => {
        setIsLoading(true);
        try {
            if(!metricList || metricList.length === 0) {
                await fetchAllMetrics();
            }
        } catch (error) {
            console.error("Error fetching metrics:", error);
        } finally {
            setIsLoading(false);
        }
    };
    
    loadMetrics();
}, []);

  const handleMetricClick = (metric: Header) => {
    setSelectedMetric(metric);
    setSelectedBenchmark(null);
    setSelectedKgColumn(null);
  };

  // Filter metrics based on search query
  const filteredMetrics = metricList.filter(metric =>
    metric?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const closeCreateMetric = () => {
    setCreateMetricFlag(false);
    setOpenCreateMetricForm(false);
    setSelectedModelId("");
    metricModalState.onClose();
  };

  const handleCreateMetric = async (id: string) => {
    if(id === "") {
    return;
    }
    const data = await getModelById(id);
    if (data) {
    setModelInfoData(data);
    }
    setOpenCreateMetricForm(true);
  };

  const openCreateMetricModal = () => {
    setSelectedMetric(null as any);
    setCreateMetricFlag(true);
    metricModalState.onOpen();
  };

  // Function to trigger submit
  const handleSave = () => {
    setTriggerSubmit(true);
  };

  const saveButton = (
    <Button
      colorScheme="green"
      size="sm"
      onClick={handleSave}
      leftIcon={<FaCheckCircle />}
      fontWeight="bold"
    >
      Save
    </Button>
  );
  

  return (
    <>
    <Box height="100vh" display="flex" flexDirection="column">
      {/* Back Button, Search, and Header in same line */}
      <Box px={3} py={3} flexShrink={0}>
        <HStack spacing={3} alignItems="center" width="100%">
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon boxSize={5} />}
            variant="tertiary"
            size="xs"
            onClick={onBack}
            _hover={{ bg: "purple.50", color: "black" }}
            flexShrink={0}
            fontSize="sm"
          />

          <InputGroup flex="1" maxW="400px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search metrics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
              size="sm"
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
            />
          </InputGroup>
          
          <Button
            onClick={openCreateMetricModal}
            leftIcon={<AddIcon boxSize={3}/>}
            size="xs"
            aria-label="Create New Plan"
            borderRadius="md"
            title="Create New Plan"
            bg="gray.50"
            color="purple.700"
            _hover={{ bg: "purple.700", color: "white" }}
            variant="outline"
            borderWidth="2px"
            borderColor="purple.700"
            fontWeight="700"
            iconSpacing={1}
            cursor="pointer"
          >
            New
          </Button>
        </HStack>
      </Box>

      {/* Metrics List */}
      <Box 
        flex="1" 
        overflowY="auto"
        overflowX="hidden"
        px={4}
        pb={14}
        minHeight={0}
        sx={{
          "::-webkit-scrollbar": {
            width: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "gray.100",
            borderRadius: "3px",
          },
          "::-webkit-scrollbar-thumb": {
            background: "purple.300",
            borderRadius: "3px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "purple.400",
          },
        }}
      >
        {isLoading ? (
          <Center height="100%">
            <Spinner size="md" color="purple.500" />
          </Center>
        ) : (
          <VStack spacing={2} align="stretch">
          {/* Metrics Heading with Filter */}
          <HStack justify="space-between" align="center" mx={1} p={0}>
            <Icon as={MdBarChart} boxSize={5} color="gray.500"/>
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              Metrics
            </Text>
            {/* Divider Line */}
            <Box 
              flex="1" 
              height="1px" 
              bg="gray.300" 
              mx={0}
            />
            <IconButton
              aria-label="Filter metrics"
              icon={<Icon as={FaFilter} />}
              size="xs"
              variant="ghost"
              color="gray.500"
              _hover={{ color: "purple.600", bg: "gray.50" }}
              cursor="pointer"
            />
          </HStack>
            {filteredMetrics.length > 0 ? (
              filteredMetrics.map((metric) => (
                <Button
                  key={metric.id}
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  borderColor="gray.300"
                  _hover={{ 
                    bg: selectedMetric?.id === metric.id ? "purple.700" : "purple.50", 
                    color: selectedMetric?.id === metric.id ? "white" : "gray.800"
                  }}
                  bg={selectedMetric?.id === metric.id ? "purple.700" : "white"}
                  color={selectedMetric?.id === metric.id ? "white" : "gray.800"}
                  borderRadius="md"
                  fontSize="sm"
                  height="auto"
                  py={1}
                  whiteSpace="normal"
                  textAlign="left"
                  onClick={() => handleMetricClick(metric)}
                >
                  <HStack
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Text mb={0}>{metric.name}</Text>
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            aria-label="Options"
                            borderRadius="full"
                            icon={<ChevronDownIcon/>}
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                if(showMenu===true){
                                    setShowMenu(false);
                                } else {
                                    setShowMenu(true);
                                }
                            }}
                        />
                        <MenuList
                        bg="white" 
                        border="1px solid" 
                        borderColor="gray.200"
                        boxShadow="md"
                        >
                        <MenuItem
                            fontSize="sm"
                            fontWeight={600}
                            icon={<MdEdit color="gray.700"/>}
                            onClick={() => {
                              setSelectedMetric(null as any);
                              handleEditMetricDefinitionModal(metric);
                            }}
                            cursor="pointer"
                            color="gray.800"
                            _hover={{ bg: "gray.50" }}
                            py={2}
                            px={3}
                        >
                            Definition
                        </MenuItem>
                        <MenuItem
                            fontSize="sm"
                            fontWeight={600}
                            icon={<BiNetworkChart />}
                            onClick={() => {
                              setSelectedMetric(null as any);
                              handleEditMKGModal(metric, metric?.owner_id!);
                            }}
                            cursor="pointer"
                            color="gray.800"
                            _hover={{ bg: "gray.50" }}
                            py={2}
                            px={3}
                        >
                            Edges
                        </MenuItem>
                        <MenuItem
                            fontSize="sm"
                            fontWeight={600}
                            icon={<FaEye />}
                            onClick={() => {
                              setSelectedMetric(null as any);
                              handleViewMKGModal(metric);
                            }}
                            cursor="pointer"
                            color="gray.800"
                            _hover={{ bg: "gray.50" }}
                            py={2}
                            px={3}
                        >
                            Subgraph
                        </MenuItem>
                        <MenuItem
                            fontSize="sm"
                            fontWeight={600}
                            icon={<DeleteIcon />}
                            onClick={() => {
                              setSelectedMetric(null as any);
                              handleDeleteMetricDialog(metric.id!);
                            }}
                            cursor="pointer"
                            color="red.500"
                            _hover={{ bg: "gray.50" }}
                            py={2}
                            px={3}
                        >
                            Delete
                        </MenuItem>
                        </MenuList>
                    </Menu>
                  </HStack>
                </Button>
              ))
            ) : (
              <Center py={8}>
                <Text color="gray.500">
                  {searchQuery.length > 0 ? `No metrics found matching "${searchQuery}"` : "No metrics found"}
                </Text>
              </Center>
            )}
          </VStack>
        )}
      </Box>
    </Box>
    
    {/* Modals */}
    {editMKG && (
        <CustomModal
            isOpen={metricModalState.isOpen}
            onClose={closeModal}
            title={`Edit Knowledge Graph for: ${selectedMetric?.name}`}
            maxWidth="90vw"
            maxHeight="90vh"
            headerAction={saveButton}
        >
            <EditMKGContainer
                metricInfo={metricInfo}
                triggerSubmit={triggerSubmit}
                setTriggerSubmit={setTriggerSubmit}
                onReloadKnowledgeGraph={onReloadKnowledgeGraph}
            />
        </CustomModal>
    )}

    {createMetricFlag && (
        <CustomModal
            isOpen={metricModalState.isOpen}
            onClose={closeCreateMetric}
            title={
                <>
                {modelInfoData ? 
                <Text fontWeight="bold" display="inline" color="purple.700">{modelInfoData?.header?.name} </Text>
                :
                <Text fontWeight="bold" display="inline" color="purple.700">Select Model </Text>
                }
                </>
            }
            maxWidth="50vw"
            maxHeight="90vh"
            isCentered={true}
        >
            <VStack>
            <FormControl maxW="md">
            <HStack pb="15px">
            <Select
                placeholder="Select a model"
                value={selectedModelId}
                onChange={(e) => {
                    setSelectedModelId(e.target.value);
                    handleCreateMetric(e.target.value);
                }}
            >
                {modelList.map((model) => (
                <option key={model.id} value={model.id}>
                    {model.name}
                </option>
                ))}
            </Select>
            
            </HStack>
        </FormControl>
        {openCreateMetricForm && (
            <EditMetricDefinitionContainer
                createMetricFlag={true}
                modelInfo={modelInfoData}
                metricHeader={null}
                onClose={closeCreateMetric}
                onReloadKnowledgeGraph={onReloadKnowledgeGraph}
            />
        )}
        </VStack>
        </CustomModal>
    )}
    
    {editMetricDefinition && (
        <CustomModal
            isOpen={metricModalState.isOpen}
            onClose={closeModal}
            title={
              <>
                Edit Metric Definition for: <Text fontWeight="bold" display="inline">{selectedMetric?.name}</Text>
              </>
            }
            maxWidth="90vw"
            maxHeight="90vh"
            headerAction={saveButton}
        >
            <EditMetricDefinitionContainer
              createMetricFlag={false}
              modelInfo={null}
              metricHeader={selectedMetric}
              onClose={metricModalState.onClose}
              triggerSubmit={triggerSubmit}
              setTriggerSubmit={setTriggerSubmit}
              onReloadKnowledgeGraph={onReloadKnowledgeGraph}
            />
        </CustomModal>
    )}

    {viewMKG && (
        <CustomModal
            isOpen={metricModalState.isOpen}
            onClose={closeModal}
            title={`Knowledge Graph for: ${selectedMetric?.name}`}
            maxWidth="90vw"
            maxHeight="90vh"
        >
            <MKGVisualizerContainer metricId={selectedMetric!} />
        </CustomModal>
    )}
    {deleteMetricDialog && (
      <AlertDialog
        isOpen={deleteMetricDialog}
        leastDestructiveRef={cancelDeleteRef}
        onClose={closeDeleteMetricDialog}
      >
        <AlertDialogOverlay
            style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
        >
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Metric
            </AlertDialogHeader>

            <AlertDialogBody>
                Are you sure you want to delete this metric?
            </AlertDialogBody>

            <AlertDialogFooter>
                <Button
                ref={cancelDeleteRef}
                colorScheme="green"
                onClick={closeDeleteMetricDialog}
                >
                Cancel
                </Button>
                <Button
                colorScheme="red"
                onClick={() => handleDeleteMetric(selectedMetric?.id!)}
                ml={3}
                >
                Delete
                </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    )}
    <DependenciesModal
      isOpen={showDependenciesModal}
      onClose={closeDeleteMetricModal}
      dependencies={dependencies}
      onViewMKG={handleViewMKGModal}
    />
    </>
  );
};

export default MetricListComponent;
